import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { get } from "../utils/api";
import { useAppSelector } from "./index";

const LikeContext = createContext({
  likedIds: {},
  isLiked: () => false,
  addLike: () => {},
  removeLike: () => {},
});

/* type → backend response key mapping */
const TYPE_KEY_MAP = {
  TRACKS: "tracks",
  TRACK: "tracks",
  ANNOUNCEMENT: "announcements",
  ANNOUNCEMENTS: "announcements",
  PRODUCTS: "products",
  PRODUCT: "products",
  NEWS: "news",
  STUDIO: "studios",
  STUDIOS: "studios",
};

export const LikeProvider = ({ children }) => {
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const [likedIds, setLikedIds] = useState({
    tracks: [],
    announcements: [],
    products: [],
    news: [],
    studios: [],
  });

  const fetchLikes = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const data = await get("/likes/my/");
      if (data && typeof data === "object") {
        setLikedIds({
          tracks: Array.isArray(data.tracks) ? data.tracks.map(Number) : [],
          announcements: Array.isArray(data.announcements) ? data.announcements.map(Number) : [],
          products: Array.isArray(data.products) ? data.products.map(Number) : [],
          news: Array.isArray(data.news) ? data.news.map(Number) : [],
          studios: Array.isArray(data.studios) ? data.studios.map(Number) : [],
        });
      }
    } catch (e) {
      console.error("Like fetch hatası:", e);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    fetchLikes();
  }, [fetchLikes]);

  const isLiked = useCallback(
    (type, id) => {
      if (!id) return false;
      const key = TYPE_KEY_MAP[type];
      const list = likedIds[key];
      if (!key || !Array.isArray(list)) return false;
      const numId = Number(id);
      return list.some((item) => Number(item) === numId);
    },
    [likedIds]
  );

  const addLike = useCallback((type, id) => {
    if (!id) return;
    const key = TYPE_KEY_MAP[type];
    if (!key) return;
    const numId = Number(id);
    setLikedIds((prev) => {
      const currentList = Array.isArray(prev[key]) ? prev[key] : [];
      if (currentList.some((item) => Number(item) === numId)) {
        return prev;
      }
      return {
        ...prev,
        [key]: [...currentList, numId],
      };
    });
  }, []);

  const removeLike = useCallback((type, id) => {
    if (!id) return;
    const key = TYPE_KEY_MAP[type];
    if (!key) return;
    const numId = Number(id);
    setLikedIds((prev) => {
      const currentList = Array.isArray(prev[key]) ? prev[key] : [];
      return {
        ...prev,
        [key]: currentList.filter((item) => Number(item) !== numId),
      };
    });
  }, []);

  return (
    <LikeContext.Provider value={{ likedIds, isLiked, addLike, removeLike }}>
      {children}
    </LikeContext.Provider>
  );
};

export const useLikes = () => useContext(LikeContext);
